import React, { useEffect, useMemo, useRef, useState } from 'react';

interface DraggablePanelProps {
  position: { x: number; y: number };
  width?: number;
  title: string;
  subtitle?: string;
  headerColor?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Controls sitting in the header bar, left of the close button. */
  headerActions?: React.ReactNode;
  zIndex?: number;
  contentMaxHeight?: number | string;
}

const PADDING = 16;

/**
 * A reusable draggable panel component with a drag handle header.
 * Used for floating popovers that can be positioned and dragged by the user.
 */
const DraggablePanel: React.FC<DraggablePanelProps> = ({
  position,
  width = 360,
  title,
  subtitle,
  headerColor = '#eff6ff',
  onClose,
  headerActions,
  children,
  footer,
  zIndex = 1100,
  contentMaxHeight,
}) => {
  // Compute initial position with viewport clamping
  const computed = useMemo(() => {
    let left = position.x + 16;
    let top = position.y + 10;

    if (left + width > window.innerWidth - PADDING) {
      left = Math.max(PADDING, position.x - width - 16);
    }
    if (top < PADDING) top = PADDING;
    if (top + 320 > window.innerHeight - PADDING) {
      top = Math.max(PADDING, window.innerHeight - PADDING - 320);
    }

    return { left, top };
  }, [position.x, position.y, width]);

  const hasDraggedRef = useRef(false);
  const [panelPos, setPanelPos] = useState(() => computed);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number } | null>(null);

  // Update position when computed changes (unless user has dragged)
  useEffect(() => {
    if (hasDraggedRef.current) return;
    setPanelPos(computed);
  }, [computed.left, computed.top]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('[data-no-drag]')) return;

    hasDraggedRef.current = true;
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: panelPos.left,
      startTop: panelPos.top,
    };

    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    if (!dragRef.current) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    const nextLeft = dragRef.current.startLeft + dx;
    const nextTop = dragRef.current.startTop + dy;

    // Keep the panel within the viewport with padding
    const clampedLeft = Math.min(
      window.innerWidth - PADDING - width,
      Math.max(PADDING, nextLeft)
    );
    const clampedTop = Math.min(
      window.innerHeight - PADDING - 80,
      Math.max(PADDING, nextTop)
    );

    setPanelPos({ left: clampedLeft, top: clampedTop });
    e.preventDefault();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const handlePointerCancel = () => {
    setIsDragging(false);
    dragRef.current = null;
  };

  return (
    <div
      tabIndex={-1}
      onKeyDownCapture={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        left: panelPos.left,
        top: panelPos.top,
        zIndex,
        width,
        background: 'white',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Draggable Header */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
          background: headerColor,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 10, color: '#4b5563', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {headerActions}
          <button
            type="button"
            onClick={onClose}
            data-no-drag="true"
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              color: '#6b7280',
            }}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          maxHeight: contentMaxHeight,
          overflowY: contentMaxHeight ? 'auto' : 'visible',
        }}
      >
        {children}
      </div>

      {/* Optional Footer */}
      {footer && (
        <div
          style={{
            padding: '10px 12px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            background: '#f9fafb',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

export default DraggablePanel;
